import {
  Text,
  Button,
  GreyGrey30,
  IconImages,
  IconTrashCan,
  Image,
  Primary13Regular,
  RedRed50,
  Box,
  Flex
} from '@sequencehq/core-components'
import type { ServerAsset } from '@sequencehq/api'
import { UploadAssetButton } from 'Assets/index'
import {
  FeedbackAttachment,
  TOO_MANY_ATTACHMENTS_ERROR
} from 'components/ShareFeedbackModal/useShareFeedbackModal'
import { AnimatePresence, motion } from 'framer-motion'
import { FC } from 'react'

// Attachments are relayed to Slack as inline image blocks, so restrict to common image formats.
const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg'

type Props = {
  attachments: FeedbackAttachment[]
  remainingSlots: number
  isUploading: boolean
  error: string | null
  onFilesPicked: () => void
  onUploadSuccess: (asset: ServerAsset) => void
  onUploadFailure: (error: string) => void
  onUploadComplete: () => void
  onRemove: (index: number) => void
}

export const FeedbackAttachments: FC<Props> = ({
  attachments,
  remainingSlots,
  isUploading,
  error,
  onFilesPicked,
  onUploadSuccess,
  onUploadFailure,
  onUploadComplete,
  onRemove
}) => {
  const canAddMore = remainingSlots > 0

  return (
    <Flex flexDir="column" gap="8px">
      {attachments.length > 0 && (
        <Flex
          gap="8px"
          flexWrap="wrap"
          data-testid="share-feedback-modal.attachments"
        >
          <AnimatePresence initial={false}>
            {attachments.map((attachment, index) => (
              <motion.div
                key={attachment.uid}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.15 }}
              >
                <AttachmentThumbnail
                  attachment={attachment.asset}
                  onRemove={() => onRemove(index)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </Flex>
      )}
      {canAddMore && (
        <UploadAssetButton
          variant="secondary"
          leadingIcon={<IconImages />}
          accept={ACCEPTED_IMAGE_TYPES}
          multiple
          maximumNumberOfFiles={remainingSlots}
          maximumNumberOfFilesError={TOO_MANY_ATTACHMENTS_ERROR}
          submitting={isUploading}
          onFilesPicked={onFilesPicked}
          onUploadSuccess={onUploadSuccess}
          onUploadFailure={onUploadFailure}
          onAllUploadsComplete={onUploadComplete}
          data-testid="share-feedback-modal.add-attachment"
        >
          Add image
        </UploadAssetButton>
      )}
      {error && (
        <Text
          {...Primary13Regular}
          color={RedRed50}
          data-testid="share-feedback-modal.attachment-error"
        >
          {error}
        </Text>
      )}
    </Flex>
  )
}

const AttachmentThumbnail: FC<{
  attachment: ServerAsset
  onRemove: () => void
}> = ({ attachment, onRemove }) => (
  <Box
    position="relative"
    role="group"
    width="64px"
    height="64px"
    borderRadius="6px"
    overflow="hidden"
    border={`1px solid ${GreyGrey30}`}
  >
    <Image
      src={attachment.url}
      alt={attachment.fileName}
      width="100%"
      height="100%"
      objectFit="cover"
    />
    <Box
      position="absolute"
      top="2px"
      right="2px"
      opacity={0}
      _groupHover={{ opacity: 1 }}
      transition="opacity 0.15s ease-out"
    >
      <Button
        variant="secondary"
        size="24px"
        icon={<IconTrashCan />}
        aria-label={`Remove ${attachment.fileName}`}
        onClick={onRemove}
        data-testid="share-feedback-modal.attachment.remove"
      />
    </Box>
  </Box>
)
